import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown, Search } from 'lucide-react';
import { createPortal } from "react-dom";


const CustomMultiSelect = ({
  options = [],
  value = [],
  onChange,
  onClose,
  placeholder = "Select options",
  label = "",
  disabled = false,
  loading = false,
  noOptionsText = "No options found",
  showSelectAll = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const PRIMARY_COLOR = "#6366f2";
  const menuRef = useRef(null);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutsideTrigger =
        dropdownRef.current && !dropdownRef.current.contains(event.target);
  
      const clickedOutsideMenu =
        menuRef.current && !menuRef.current.contains(event.target);
  
      if (clickedOutsideTrigger && clickedOutsideMenu) {
        setIsOpen(false);
        if (onClose) onClose();
      }
    };
  
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);
  

  // Filter options based on search
 const filteredOptions = (() => {
  const input = searchTerm?.toLowerCase();
  if (!input) return options;
  const startsWith = options.filter(o => o?.label?.toLowerCase().startsWith(input));
  const contains = options.filter(o =>
    !o?.label?.toLowerCase().startsWith(input) &&
    o?.label?.toLowerCase().includes(input)
  );
  return [...startsWith, ...contains];
})();

  // Check if option is selected
  const isSelected = (optionValue) => {
    return value.some(v => v.value === optionValue);
  };

  // Toggle option selection
  const toggleOption = (option) => {
    if (disabled) return;

    const isCurrentlySelected = isSelected(option.value);
    let newValue;

    if (isCurrentlySelected) {
      newValue = value.filter(v => v.value !== option.value);
    } else {
      newValue = [...value, option];
    }

    onChange(null, newValue);
  };

  // Remove selected chip
  const removeChip = (optionValue, e) => {
    e.stopPropagation();
    const newValue = value.filter(v => v.value !== optionValue);
    onChange(null, newValue);
  };

  // Clear all selections
  const clearAll = (e) => {
    e.stopPropagation();
    onChange(null, []);
  };

  // Select all filtered options
  const selectAll = () => {
    if (disabled) return;
    onChange(null, filteredOptions);
  };

  // Check if all filtered options are selected
  const allSelected = filteredOptions.length > 0 && filteredOptions.every(option => 
    value.some(v => v.value === option.value)
  );

  // Check if some but not all are selected
  const someSelected = value.length > 0 && !allSelected;

  return (
    <div style={{ marginBottom: '16px', width: '100%' }}>
      {label && (
        <div style={{
          fontSize: '12px',
          fontWeight: 600,
          marginBottom: '4px',
          color: '#374151'
        }}>
          {label}
        </div>
      )}

      <div ref={dropdownRef} style={{ position: 'relative' }}>
        {/* Main input container */}
        <div
          onClick={() => !disabled && setIsOpen(!isOpen)}
          style={{
            minHeight: '35px',
            backgroundColor: disabled ? '#F3F4F6' : '#fff',
            border: `${isOpen ? 2 : 1}px solid ${isOpen ? PRIMARY_COLOR : '#d1d5db'}`,
            borderRadius: '8px',
            padding: '4px 8px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '4px',
            transition: 'border-color 0.2s',
            opacity: disabled ? 0.6 : 1,
            boxShadow: isOpen ? `0 0 0 1px ${PRIMARY_COLOR}` : 'none'
          }}
          onMouseEnter={(e) => {
            if (!disabled && !isOpen) {
              e.currentTarget.style.borderColor = PRIMARY_COLOR;
            }
          }}
          onMouseLeave={(e) => {
            if (!isOpen) {
              e.currentTarget.style.borderColor = '#d1d5db';
            }
          }}
        >
          {/* Selected chips */}
          {value.map((item) => (
            <div
              key={item.value}
              style={{
                height: '24px',
                backgroundColor: PRIMARY_COLOR,
                color: '#ffffff',
                borderRadius: '4px',
                padding: '2px 8px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                margin: '2px'
              }}
            >
              <span style={{ whiteSpace: 'nowrap' }}>{item.label}</span>
              <X
                size={14}
                onClick={(e) => removeChip(item.value, e)}
                style={{
                  cursor: 'pointer',
                  opacity: 0.8,
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                onMouseLeave={(e) => e.currentTarget.style.opacity = 0.8}
              />
            </div>
          ))}

          {/* Placeholder */}
          {value.length === 0 && (
            <span style={{
              color: '#9CA3AF',
              fontSize: '12px',
              padding: '4px',
              flex: 1
            }}>
              {loading ? `Loading...` : placeholder}
            </span>
          )}

          {/* Spacer */}
          <div style={{ flex: 1, minWidth: '20px' }} />

          {/* Clear button */}
          {value.length > 0 && !disabled && (
            <X
              size={16}
              onClick={clearAll}
              style={{
                color: '#6B7280',
                cursor: 'pointer',
                padding: '2px',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#374151'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#6B7280'}
            />
          )}

          {/* Dropdown arrow */}
          <ChevronDown
            size={16}
            style={{
              color: '#6B7280',
              transition: 'transform 0.2s',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          />
        </div>

        {/* Dropdown menu */}
        {isOpen && createPortal(
          <div
          ref={menuRef} 
          style={{
            position: "absolute",
            top: dropdownRef.current?.getBoundingClientRect().bottom + window.scrollY,
            left: dropdownRef.current?.getBoundingClientRect().left + window.scrollX,
            width: dropdownRef.current?.getBoundingClientRect().width,
            backgroundColor: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            boxShadow: "0 4px 14px rgba(15,23,42,0.1)",
            maxHeight: "300px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            zIndex: 9999999,   // Now truly on top
          }}
        >
        
            {/* Search box */}
            <div style={{
              padding: '8px',
              borderBottom: '1px solid #E5E7EB',
              position: 'sticky',
              top: 0,
              backgroundColor: '#fff',
              zIndex: 1
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 8px',
                backgroundColor: '#F9FAFB',
                borderRadius: '6px',
                border: '1px solid #E5E7EB'
              }}>
                <Search size={14} style={{ color: '#9CA3AF' }} />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={placeholder}
                  style={{
                    border: 'none',
                    outline: 'none',
                    backgroundColor: 'transparent',
                    fontSize: '12px',
                    width: '100%',
                    color: '#374151'
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* Select All option */}
            {showSelectAll && filteredOptions.length > 0 && !loading && (
              <div
                onClick={() => allSelected ? clearAll({ stopPropagation: () => {} }) : selectAll()}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: '#F9FAFB',
                  borderBottom: '1px solid #E5E7EB',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: PRIMARY_COLOR,
                  transition: 'background-color 0.1s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 0, 160, 0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#F9FAFB';
                }}
              >
                {/* Custom checkbox for Select All */}
                <div style={{
                  width: '18px',
                  height: '18px',
                  border: `2px solid ${allSelected || someSelected ? PRIMARY_COLOR : '#D1D5DB'}`,
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: allSelected ? PRIMARY_COLOR : '#fff',
                  flexShrink: 0,
                  transition: 'all 0.2s'
                }}>
                  {allSelected && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M10 3L4.5 8.5L2 6"
                        stroke="#fff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                  {someSelected && !allSelected && (
                    <div style={{
                      width: '10px',
                      height: '2px',
                      backgroundColor: PRIMARY_COLOR,
                      borderRadius: '1px'
                    }} />
                  )}
                </div>
                <span>Select All ({filteredOptions.length})</span>
              </div>
            )}

            {/* Options list */}
            <div style={{
              overflowY: 'auto',
              maxHeight: '250px'
            }}>
              {loading ? (
                <div style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: '#9CA3AF',
                  fontSize: '12px'
                }}>
                  Loading...
                </div>
              ) : filteredOptions.length === 0 ? (
                <div style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: '#9CA3AF',
                  fontSize: '12px'
                }}>
                  {noOptionsText}
                </div>
              ) : (
                filteredOptions.map((option) => {
                  const selected = isSelected(option.value);
                  return (
                    <div
                      key={option.value}
                      onClick={() => toggleOption(option)}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        backgroundColor: selected
                          ? 'rgba(0, 0, 160, 0.1)'
                          : '#fff',
                        color: selected ? PRIMARY_COLOR : '#374151',
                        fontSize: '12px',
                        transition: 'background-color 0.1s',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word'
                      }}
                      onMouseEnter={(e) => {
                        if (!selected) {
                          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 160, 0.05)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selected) {
                          e.currentTarget.style.backgroundColor = '#fff';
                        }
                      }}
                    >
                      {/* Custom checkbox */}
                      <div style={{
                        width: '18px',
                        height: '18px',
                        border: `2px solid ${selected ? PRIMARY_COLOR : '#D1D5DB'}`,
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: selected ? PRIMARY_COLOR : '#fff',
                        flexShrink: 0,
                        transition: 'all 0.2s'
                      }}>
                        {selected && (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path
                              d="M10 3L4.5 8.5L2 6"
                              stroke="#fff"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                      <span>{option.label}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div> , document.body
        )}
      </div>
    </div>
  );
};

export default CustomMultiSelect;